import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewChildren,
	QueryList,
	SimpleChanges,
	ElementRef,
	ChangeDetectorRef,
	Injector,
	TemplateRef,
} from '@angular/core';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { FormArray, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
	IS_NULL_OR_UNDEFINED,
	Owner,
	IS_NOT_NULL_OR_UNDEFINED,
	SelectedDateRange,
	IS_NOT_EMPTY,
	RestService,
	GET_CARRIER_BUSINESS_NAME,
	IS_NOT_EMPTY_LIST,
	IS_EMPTY,
	DIFFERENCE_FROM_NOW,
} from 'cargo-chief-commons';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UtilService } from '@service/util/util.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { ProductsService } from '@service/products/products.service';
import { PROJECT_FIELDS, PROJECT_STAGES } from '../../v2/project/projects-list/constants';
import { PROJECT_TYPES } from '@domain/constants/project-types';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import {
	OPERATOR_OPTIONS,
	OPERATORS,
	LOAD_VOLUME_DURATION,
	INPUT_TYPES,
	LOCATION_OPERATORS_LABEL,
	STAGE_OPERATORS_LABEL,
	ARCHIVED_OPERATORS_LABEL,
	JOB_STATUS_OPERATORS_LABEL,
	EQUAL_NOT_EQUAL_TYPES_LABEL,
	PROJECT_OPERATORS_LABEL,
	TO_YMD,
} from './constants';
import { FILTERABLE_STATES } from '@domain/constants/filterable-states';
import { AUTOMATION_LOADS } from 'app/broker/automation/loads/store/actions';
import { USER_SORTS_AND_FILTERS_TYPE } from '@domain/constants/app-variables';
import { LOAD_FIELDS } from '../../loads-sourcing/carriers-loads-list/constants';

import {
	BULK_CAPACITY_MATCH_JOBS_STATUS,
	LOADS_AUTOMATION_FIELDS,
} from 'app/broker/automation/loads/loads/constants';
import { IMPORT_STATS_JOBS_STATUS } from 'app/broker/automation/utils/import-stats-constants';
import { USER_STATUS, USER_ROLE } from '../../broker/teams/users-list/constants';

import { Router } from '@angular/router';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { UrlService } from '@service/url/url.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmTextDialogComponent } from '../confirm-text-dialog/confirm-text-dialog.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ContactDialogComponent } from '../../contact-details/contact-details-modal/contact-details-modal.component';
import { LOAD_TYPE_DROPDOWN_ITEMS, LOAD_TYPES } from '../../loads-sourcing/constants/load-type';
import { HaulpayService } from '@service/haulpay/haulpay.service';
import { CARRIER_LANES } from '../../carriers/lanes/store/actions';
import { IntegrationService } from '@service/integration/integration.service';
import { equipmentMatches } from '@shared/utils/equipment-matches';
import {
	CARRIER_DEPTH_METER,
	LANE_CONFIDENCE_METER,
	RATE_CONFIDENCE_METER,
} from '../../broker/automation/rfp/utils/bulk-rating-list-metrics';

/**
 * CN-8940: what a consumer must supply per row to render the contact controls in the
 * carrier-name cell. `loadId` and `bid.carrierId`/`bid.id` scope the logged call;
 * `quoteLane` is only used to caption the push notification.
 */
export interface ContactActionsInputs {
	bid: any;
	loadId: string;
	quoteLane: string;
}

/**
 * TabbedTableComponent - A reusable table component with filtering, sorting, and custom actions support.
 *
 * Features:
 * - Configurable actions component selector for better reusability
 * - Support for custom action components via ngComponentOutlet
 * - Row click detection that respects action component boundaries
 *
 * Usage Examples:
 *
 * 1. Using with default offer-action component (backward compatible):
 * ```html
 * <app-tabbed-table
 *   [customActionsComponent]="OfferActionComponent"
 *   [customActionsInputs]="customActionsInputs"
 *   [customActionsOutputs]="customActionsOutputs">
 * </app-tabbed-table>
 * ```
 *
 * 2. Using with custom bid-action component:
 * ```html
 * <app-tabbed-table
 *   [customActionsComponent]="BidActionComponent"
 *   [actionsComponentSelector]="'app-bid-action'"
 *   [customActionsInputs]="bidActionsInputs"
 *   [customActionsOutputs]="bidActionsOutputs">
 * </app-tabbed-table>
 * ```
 *
 * 3. Using with any custom action component:
 * ```html
 * <app-tabbed-table
 *   [customActionsComponent]="MyCustomActionComponent"
 *   [actionsComponentSelector]="'app-my-custom-action'"
 *   [customActionsInputs]="myCustomActionsInputs"
 *   [customActionsOutputs]="myCustomActionsOutputs">
 * </app-tabbed-table>
 * ```
 *
 * The actionsComponentSelector property allows you to specify the CSS selector
 * used to identify action components in the row click detection logic. This
 * prevents row clicks from being triggered when users interact with action buttons.
 */
@Component({
	selector: 'app-tabbed-table',
	templateUrl: './tabbed-table.component.html',
	styleUrls: ['./tabbed-table.component.scss'],
})
export class TabbedTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	compareEquipment = equipmentMatches;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('tabHeaderScroll', { static: false }) tabHeaderScroll!: ElementRef;
	@ViewChildren('tabLinks') tabLinks!: QueryList<ElementRef>;

	// actions input
	@Input() showAddProjectsButton: boolean = false;
	@Input() addProjectsButtonText: string = 'New Project';
	@Input() showImportProjectsButton: boolean = false;
	@Input() showImportLoadsButton: boolean = false;
	@Input() showImportTransactionsButton: boolean = false;
	@Input() showImportCarrierButton: boolean = false;
	@Input() showImportRFPButton: boolean = false;
	// CN-8861: label for the import button. Defaults to today's "Run RFP" so the
	// button is unchanged when Bulk Rating is off; the RFP screen passes
	// "Run Bulk Rating" when the account setting is on.
	@Input() importRFPButtonLabel: string = 'Run RFP';
	@Input() disableActions: boolean = true;

	@Input() showUpdateViewButton: boolean = true;
	@Input() showSaveViewButton: boolean = true;
	@Input() showFilterButton: boolean = true;
	@Input() showSortButton: boolean = true;
	@Input() enableFilterButton: boolean = true;
	@Input() enableSortButton: boolean = true;
	@Input() showActionButton: boolean = true;
	@Input() rowSelectableEnabled: boolean = true;
	@Input() selectAllEnabled: boolean = true;
	@Input() actionTextDescriptions: string[] = [];
	@Input() waitingMessage: string = null;

	@Input() importLoads: (args: any) => void;
	@Input() importTransactions: (args: any) => void;
	@Input() importRFPs: (args: any) => void;
	@Input() applyRowClass: (args: any) => string = () => '';

	@Input() links: string[] = [];
	@Input() activeLink: string;
	@Input() customTabId: number;
	@Input() actions = [];
	@Input() data: Array<Object> = [];
	@Input() dataType: string = 'data';
	@Input() headers: Array<{ dataName: string; formattedName: string; action?: (element) => void }> =
		[];
	@Input() displayedColumns: string[] = [];
	@Input() dataTypes: string[] = [];
	@Input() customDataTypes: { [columnName: string]: string } = {};

	// CN-8872: the Bulk Rating meter bands, so the meter render-types bind to the SAME source
	// of truth as carrierDepthColorClass (the per-lane count coloring) — no drift when the
	// depth bands are retuned (CN-8874).
	readonly rateConfidenceMeter = RATE_CONFIDENCE_METER;
	readonly carrierDepthMeter = CARRIER_DEPTH_METER;
	// CN-9039: the job-level Lane Confidence average, banded from the same LANE_CONFIDENCE_GREEN /
	// _AMBER thresholds the per-lane meter uses, so the list and the detail grid cannot disagree
	// about what "green" means.
	readonly laneConfidenceMeter = LANE_CONFIDENCE_METER;
	@Input() fieldMap: Object;
	@Input() filterFields: string[];
	@Input() filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();
	@Input() filterFieldsInputLabelMap: Map<string, any> = new Map<string, any>();
	@Input() sortFields: string[];
	@Input() isStaticData: boolean = true;
	@Input() loadingData: boolean = false;
	@Input() showProgressBar: boolean = false;
	@Input() totalNumberOfRecords: number = 0;
	@Input() activeUsersList: Array<Owner> = [];
	@Input() usersListPlaceholder: string = 'Assigned To';
	@Input() showPreviousDates: boolean = false;
	@Input() showScorecardRange: boolean = false;
	// CN-8491: When the consumer needs both past and future date selection (Booked Loads),
	// pass `allowAnyDate=true` and supply a `dateRangePresets` map of preset shortcuts.
	@Input() allowAnyDate: boolean = false;
	@Input() dateRangePresets: any = null;
	@Input() showTabBars: boolean = true;
	@Input() useDropdownTabs: boolean = false;
	@Input() rowClasses: string = '';
	@Input() rowOnClickEnabled: boolean = false;
	@Input() showFooterText: boolean = false;
	@Input() footerText!: string;
	@Input() showPostLoadButton: boolean = false;
	@Input() userSortsAndFiltersType!: string;
	@Input() isActiveTab: boolean = false;
	@Input() isOpenTab: boolean = false;
	@Input() isBookedTab: boolean = false;
	@Input() isFullWidthActionRow: boolean = false;
	@Input() disableTabNavPagination: boolean = false;
	@Input() disableMultipleSelection: boolean = false;
	@Input() tabScrollEnabled: boolean = false;
	@Input() removeTableContainerPadding: boolean = false;
	@Input() companyList: any = [];

	@Input() rowOnClick: (args: any) => void;
	@Input() postLoadOnClick: () => void;

	@Input() rowCheckboxIsDisabled: (args: any) => boolean;
	@Input() rowOnSelect: (args: any, state: boolean, currentSelection?: any[]) => void;
	/**
	 * CN-8950: opt in to having the header select-all checkbox notify `rowOnSelect`.
	 *
	 * Off by default because `rowOnSelect` implementations that dereference the row
	 * argument (carriers-loads-list, lanes-list) would change behaviour on select-all.
	 * Lists whose `rowOnSelect` only reads `currentSelection` — and therefore need the
	 * callback to rebuild their selection-dependent actions — turn this on.
	 */
	@Input() notifyRowOnSelectAll: boolean = false;
	@Input() getUserSortsAndFilters: () => void;

	@Input() customActionsComponent: any = null;
	@Input() customActionsInputs: (element: any) => any = null;
	@Input() customActionsOutputs: any = null;
	@Input() promptActionsText!: string;
	/**
	 * Configurable CSS selector for the actions component used in row click detection.
	 * This allows the component to identify when a click occurs within the actions column
	 * to prevent triggering row click events. Defaults to 'app-offer-action' for backward compatibility.
	 * Can be overridden when using different action components.
	 */
	@Input() actionsComponentSelector: string = 'app-offer-action'; // Configurable selector for actions component
	@Input() carrierIdForNetworkAccountsFilter: string = null;

	// Carrier modal functionality inputs
	@Input() enableCarrierModal: boolean = false;
	@Input() carrierModalInputs: (element: any) => any = null;

	// CN-8940: contact controls (call / email / Log Call) rendered inside the
	// carrierNameWithNetwork cell. Opt-in, and the consumer supplies the mapper so
	// the shared table never has to know the row's field names.
	@Input() showContactActions: boolean = false;
	@Input() contactActionsInputs: (element: any) => ContactActionsInputs = null;
	@Input() selectedRowIds: any[] = null;
	@Input() showLoadTypeDropdown: boolean = false;
	@Input() selectedLoadType: string = LOAD_TYPES.MATCHED_LOADS;
	@Input() haulPayTooltipForBrokers: boolean = false;

	// Tooltip configuration inputs
	@Input() enableRowTooltips: boolean = false;
	@Input() tooltipDataFlag: string = 'showTooltip'; // Flag in data to determine if tooltip should show
	@Input() tooltipContent: string = ''; // Tooltip text

	// Lighter text styling configuration inputs
	@Input() enableLighterText: boolean = false;
	@Input() lighterTextDataFlag: string = 'lighterText'; // Flag in data to determine if text should be lighter
	@Input() lighterTextClass: string = 'lighter-text'; // CSS class to apply for lighter text
	@Input() emptyDataMessage: string = '';
	@Input() showEmptyDataSpinner: boolean = false; // Show loading spinner next to empty data message
	@Input() alwaysShowTableHeaders: boolean = false; // Always show table headers even during initial loading
	@Input() PROJECT_TYPES: any = PROJECT_TYPES;
	@Input() pageSize: number = 25; // Default page size for paginator
	@Input() enableOnboardCarrier: boolean = false;

	@Output() updateData: EventEmitter<string> = new EventEmitter();
	@Output() onOnboardCarrier: EventEmitter<any> = new EventEmitter();
	// CN-8940: a call was logged against this row and the bid is now Contacted.
	// Deliberately NOT wired to a refetch by the Offers list: the contact controls
	// already patch the bid in the store, so the row updates in place. Refetching
	// re-sorts it under the new status and moves it off the page the rep is on, which
	// reads as the offer disappearing.
	@Output() contactBidContacted: EventEmitter<any> = new EventEmitter();
	@Output() pageEvent: EventEmitter<{ page: number; pageSize: number }> = new EventEmitter();
	@Output() emitClearFilter: EventEmitter<string> = new EventEmitter();
	@Output() emitClearSort: EventEmitter<string> = new EventEmitter();
	@Output() emitFilter: EventEmitter<Array<Object>> = new EventEmitter();
	@Output() emitSort: EventEmitter<Array<Object>> = new EventEmitter();
	@Output() importData: EventEmitter<string> = new EventEmitter();
	@Output() createData: EventEmitter<string> = new EventEmitter();
	@Output() emitLoadTypeToggled: EventEmitter<string> = new EventEmitter();
	@Output() handledSelectedRows: EventEmitter<void> = new EventEmitter();
	@Output() carriersCSVUploaded: EventEmitter<boolean> = new EventEmitter();
	@ViewChildren(MtxPopoverTrigger) triggers: QueryList<MtxPopoverTrigger>;

	PAGE_SIZE_OPTIONS = [25, 50, 100];
	AND = 'and';
	OR = 'or';
	FIRST_TO_LAST = 'asc';
	LAST_TO_FIRST = 'desc';

	defaultCondition = 'and';
	defaultDirection = 'asc';

	filterState = '';
	sortState = '';

	isActionbuttonDisabled = true;

	isAddConditionActive: boolean = false;
	isFilterButtonActive: boolean = false;
	filterForm: FormGroup;

	isAddFieldActive: boolean = false;
	isSortButtonActive: boolean = false;
	sortForm: FormGroup;

	dataSource: MatTableDataSource<Object>;
	selection = new SelectionModel<Object>(true, []);

	trailerTypes: any[] = [];
	selectedUsers = [];
	minimumDate = new Date();
	locationModel = {};
	filterOptions: Array<Observable<string[]>> = [];
	sortOptions: Array<Observable<string[]>> = [];
	tabHeadersMaxWidth!: number;

	saveViewActions = [];

	OPERATOR_OPTIONS = OPERATOR_OPTIONS;
	OPERATORS = OPERATORS;
	LOAD_VOLUME_DURATION = LOAD_VOLUME_DURATION;

	stateOptions = FILTERABLE_STATES();
	LOCATION_OPERATORS_LABEL = LOCATION_OPERATORS_LABEL;
	STAGE_OPERATORS_LABEL = STAGE_OPERATORS_LABEL;
	JOB_STATUS_OPERATORS_LABEL = JOB_STATUS_OPERATORS_LABEL;
	ARCHIVED_OPERATORS_LABEL = ARCHIVED_OPERATORS_LABEL;
	EQUAL_NOT_EQUAL_TYPES_LABEL = EQUAL_NOT_EQUAL_TYPES_LABEL;
	PROJECT_STAGES: Array<any> = PROJECT_STAGES;
	BULK_CAPACITY_MATCH_JOBS_STATUS = BULK_CAPACITY_MATCH_JOBS_STATUS;
	IMPORT_STATS_JOBS_STATUS = IMPORT_STATS_JOBS_STATUS;
	PROJECT_OPERATORS_LABEL = PROJECT_OPERATORS_LABEL;
	LOAD_TYPE_DROPDOWN_ITEMS = LOAD_TYPE_DROPDOWN_ITEMS;
	USER_STATUS = USER_STATUS;
	USER_ROLE = USER_ROLE;

	// actions for bulk upload
	AUTOMATION_LOADS = AUTOMATION_LOADS;

	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_EMPTY = IS_EMPTY;
	DIFFERENCE_FROM_NOW = DIFFERENCE_FROM_NOW;

	removeContainerHorizontalPadding: boolean = false;
	isSmallScreen: boolean = false;

	TAB_HEADER_NAVIGATION_BUTTONS_WIDTH: number = 56;
	isAtStart = true;
	isAtEnd = false;
	selectedTabIndex: number = 0;
	trackByIndex = (index: number) => index;

	// Store scroll event handler for proper cleanup
	private scrollEventHandler: (() => void) | null = null;
	private scrollPositionTimeoutId: any = null;

	renderedDataTypes: string[] = [];
	haulPayLogoUrl: string;
	haulPayLogoTooltip: string;

	constructor(
		private formBuilder: FormBuilder,
		private activeRoute: ActivatedRoute,
		public accountSettings: AccountSettingsService,
		public utilService: UtilService,
		public projectHelperService: ProjectHelperService,
		public productsService: ProductsService,
		public readonly store: Store<State>,
		private router: Router,
		private urlService: UrlService,
		private restService: RestService,
		public dialog: MatDialog,
		public integrationService: IntegrationService,
		private breakpointObserver: BreakpointObserver,
		private changeDetectorRef: ChangeDetectorRef,
		private injector: Injector,
		private readonly haulpayService: HaulpayService
	) {
		this.trailerTypes = this.accountSettings.SEARCHABLE_TRAILER_TYPES;
		this.haulPayLogoUrl = this.haulpayService.logo();
		this.breakpointObserver
			.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
			.subscribe((result) => {
				this.removeContainerHorizontalPadding =
					result.breakpoints[Breakpoints.XSmall] ||
					result.breakpoints[Breakpoints.Small] ||
					result.breakpoints[Breakpoints.Medium];
				this.isSmallScreen =
					result.breakpoints[Breakpoints.XSmall] || result.breakpoints[Breakpoints.Small];
			});
	}

	ngOnInit(): void {
		if (IS_NULL_OR_UNDEFINED(this.activeLink)) {
			this.activeLink = this.links[0];
		}

		this.filterForm = this.formBuilder.group({
			formArray: this.formBuilder.array([
				this.formBuilder.group({
					filterField: new FormControl(''),
					filterText: '',
					filterCondition: '',
					operator: '',
					startValue: '',
					endValue: '',
					isNotDeletable: false,
				}),
			]),
		});

		this.sortForm = this.formBuilder.group({
			sortFormArray: this.formBuilder.array([
				this.formBuilder.group({
					sortField: new FormControl(''),
					direction: this.defaultDirection,
				}),
			]),
		});

		this.filterForm.valueChanges.subscribe((val) => {
			this.isAddConditionActive = this.hasMoreCondition();
			this.isFilterButtonActive = this.hasAnyFilter();
		});

		this.sortForm.valueChanges.subscribe((val) => {
			this.isAddFieldActive = this.hasMoreSort();
			this.isSortButtonActive = this.hasAnySorting();
		});

		this.setLocationModel();

		this.addFilterOptions();
		this.addSortOptions();

		this.buildSaveViewActions();
	}

	buildSaveViewActions() {
		this.saveViewActions = [
			{
				label: 'Save New View',
				action: () => {
					const dialogRef = this.dialog.open(ConfirmTextDialogComponent, {
						width: '400px',
						panelClass: 'cargo_chief_dialog_container',
						data: {
							title: 'Save a new view',
							placeholder: 'Tab Name',
							errorMessage: 'Failed to save custom tab',
						},
					});

					dialogRef.afterClosed().subscribe((result) => {
						if (!this.utilService.isNullOrUndefined(result) && result) {
							this.saveCustomFilter(result.trim());
						}
					});
				},
				disabled: () => {
					return false;
				},
			},
			{
				label: 'Update View',
				action: () => {
					this.saveCustomFilter(this.activeLink.trim(), true);
				},
				disabled: () => {
					return this.activeLink === this.links[0];
				},
			},
		];
	}

	setLocationModel() {
		this.filterFieldsInputTypeMap.forEach((field, value) => {
			if (field === INPUT_TYPES.LOCATION) {
				this.locationModel[`${value}0`] = '';
			}
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['data'] && changes['data'].currentValue) {
			this.dataSource = new MatTableDataSource<Object>(this.data);
			if (this.isStaticData) {
				this.dataSource.paginator = this.paginator;
			}

			this.selection = new SelectionModel<Object>(true, []);
			this.isActionbuttonDisabled = true;
			if (this.showTabBars && this.links.length) {
				setTimeout(() => {
					this.setTabHeadersMaxWidth();
				}, 100);
			}
			if (IS_NOT_EMPTY_LIST(this.selectedRowIds)) {
				const selectedLoads = this.dataSource.data.filter((element: any) => {
					return this.selectedRowIds.includes(element.id);
				});
				for (const selectedLoad of selectedLoads) {
					this.selectRow(selectedLoad);
				}
				this.changeDetectorRef.detectChanges();
				this.handledSelectedRows.emit();
			}
		}
		if (
			(changes['dataTypes'] && this.dataTypes) ||
			(changes['customDataTypes'] && this.customDataTypes)
		) {
			// All pages exclude 'select' column from dataTypes array, so use as-is
			this.renderedDataTypes = this.rowSelectableEnabled
				? [...this.dataTypes.filter((dt) => dt !== 'select')]
				: [...this.dataTypes];

			// Apply custom data types if provided
			if (this.customDataTypes && Object.keys(this.customDataTypes).length > 0) {
				// Find the index of each custom data type column in headers.slice(1)
				this.headers.slice(this.rowSelectableEnabled ? 1 : 0).forEach((header, index) => {
					if (this.customDataTypes[header.dataName]) {
						this.renderedDataTypes[index] = this.customDataTypes[header.dataName];
					}
				});
			}
		}
	}

	ngAfterViewInit() {
		if (this.isStaticData && this.dataSource) {
			this.dataSource.paginator = this.paginator;
		}

		// Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
		setTimeout(() => {
			this.paginator.pageIndex = IS_NULL_OR_UNDEFINED(this.activeRoute.snapshot?.queryParams?.page)
				? 0
				: this.activeRoute.snapshot.queryParams.page;

			this.paginator.pageSize = IS_NULL_OR_UNDEFINED(
				this.activeRoute.snapshot?.queryParams?.pageSize
			)
				? this.pageSize
				: this.activeRoute.snapshot.queryParams.pageSize;
		}, 0);

		setTimeout(() => {
			if (!this.useDropdownTabs && this.tabHeaderScroll?.nativeElement) {
				this.checkScrollPosition();
			}
		}, 0);
		this.scrollEventHandler = () => {
			setTimeout(() => {
				if (!this.useDropdownTabs && this.tabHeaderScroll?.nativeElement) {
					this.checkScrollPosition();
				}
			}, 0);
		};
		this.tabHeaderScroll?.nativeElement?.addEventListener?.('scroll', this.scrollEventHandler);
		this.haulPayLogoTooltip = this.haulpayService.tooltip({
			forBrokers: this.haulPayTooltipForBrokers,
		});
	}

	ngOnDestroy(): void {
		// Remove scroll event listener to prevent memory leaks
		if (this.scrollEventHandler && this.tabHeaderScroll?.nativeElement) {
			this.tabHeaderScroll.nativeElement.removeEventListener('scroll', this.scrollEventHandler);
			this.scrollEventHandler = null;
		}

		// Clear any pending scroll position timeout to prevent memory leaks
		if (this.scrollPositionTimeoutId) {
			clearTimeout(this.scrollPositionTimeoutId);
			this.scrollPositionTimeoutId = null;
		}
	}

	get formArray() {
		return this.filterForm.get('formArray') as FormArray;
	}

	get sortFormArray() {
		return this.sortForm.get('sortFormArray') as FormArray;
	}

	isNumberField(index: number): boolean {
		let result = false;
		let selected = this.sortFormArray.at(index).get('sortField').value;
		result =
			this.dataTypes[this.displayedColumns.indexOf(this.fieldMap[selected]) - 1] === 'number';
		if (
			IS_NOT_EMPTY(selected) &&
			this.filterFieldsInputTypeMap?.get(selected) === INPUT_TYPES.NUMBER
		) {
			result = true;
		}
		return result;
	}

	changeActiveLink(link: string, index: number) {
		if (this.selectedTabIndex !== index) {
			this.selectedTabIndex = index;
			if (this.isStaticData) this.loadingData = true;
			this.activeLink = link;
			this.resetFilterAndSort();
			this.updateData.emit(link);
		}
		this.paginator.pageIndex = 0;
	}

	getTabIndex(link: string): number {
		return this.links.findIndex((l) => l === link);
	}

	emitPageEvent() {
		this.loadingData = true;
		this.pageEvent.emit({
			page: this.paginator.pageIndex,
			pageSize: this.paginator.pageSize,
		});
	}

	hasMoreCondition(): boolean {
		if (
			this.formArray.length > 1 &&
			this.formArray.at(1).get('filterCondition').value?.length < 1
		) {
			return false;
		}

		for (let i = 0; i < this.formArray.length; i++) {
			const isFilterInputTypeWithStartAndEndValue =
				this.formArray.at(i).get('operator').value === OPERATORS.BETWEEN;
			let isInvalidValue = true;
			if (!isFilterInputTypeWithStartAndEndValue) {
				isInvalidValue = this.formArray.at(i).get('filterText').value?.length < 1;
			} else {
				const startValue = this.formArray.at(i).get('startValue')?.value,
					endValue = this.formArray.at(i).get('endValue')?.value;
				isInvalidValue = IS_NULL_OR_UNDEFINED(startValue) || IS_NULL_OR_UNDEFINED(endValue);
			}
			if (this.formArray.at(i).get('filterField')?.value?.length < 1 || isInvalidValue) {
				return false;
			}
		}

		return true;
	}

	hasMoreSort(): boolean {
		if (this.sortFormArray.length > this.sortFields.length - 1) {
			return false;
		}

		for (let i = 0; i < this.sortFormArray.length; i++) {
			if (
				this.sortFormArray.at(i).get('sortField').value.length < 1 ||
				this.sortFormArray.at(i).get('direction').value.length < 1
			) {
				return false;
			}
		}

		return true;
	}

	hasAnyFilter(): boolean {
		for (let i = 0; i < this.formArray.length; i++) {
			const filterText = this.formArray.at(i).get('filterText').value,
				filterField = this.formArray.at(i).get('filterField').value,
				startValue = this.formArray.at(i).get('startValue')?.value,
				endValue = this.formArray.at(i).get('endValue')?.value,
				operator = this.formArray.at(i).get('operator')?.value;
			if (
				(filterField?.length > 0 &&
					((typeof filterText === 'string' && filterText?.length > 0) ||
						(typeof filterText === 'object' &&
							IS_NOT_NULL_OR_UNDEFINED(filterText) &&
							Object.keys(filterText).length > 0))) ||
				(this.filterFieldsInputTypeMap?.get(filterField || '') === 'date' &&
					(IS_NULL_OR_UNDEFINED(startValue) || IS_NULL_OR_UNDEFINED(endValue)))
			) {
				return true;
			}
			if (
				this.filterFieldsInputTypeMap?.get(filterField || '') === INPUT_TYPES.DATE &&
				startValue &&
				endValue
			) {
				return true;
			} else if (
				this.filterFieldsInputTypeMap?.get(filterField || '') === 'boolean' &&
				IS_NOT_NULL_OR_UNDEFINED(filterText)
			) {
				return true;
			} else if (this.filterFieldsInputTypeMap?.get(filterField) === INPUT_TYPES.NUMBER) {
				if (
					operator === OPERATORS.BETWEEN &&
					typeof startValue !== 'string' &&
					typeof endValue !== 'string' &&
					IS_NOT_NULL_OR_UNDEFINED(startValue) &&
					IS_NOT_NULL_OR_UNDEFINED(endValue)
				) {
					return true;
				}
				if (
					operator !== OPERATORS.BETWEEN &&
					typeof filterText !== 'string' &&
					IS_NOT_NULL_OR_UNDEFINED(filterText)
				) {
					return true;
				}
			} else if (
				this.filterFieldsInputTypeMap?.get(filterField) === INPUT_TYPES.ALLOCATED_CARRIERS
			) {
				if (
					operator === OPERATORS.BETWEEN &&
					typeof startValue !== 'string' &&
					typeof endValue !== 'string' &&
					IS_NOT_NULL_OR_UNDEFINED(startValue) &&
					IS_NOT_NULL_OR_UNDEFINED(endValue)
				) {
					return true;
				}
				if (
					operator !== OPERATORS.BETWEEN &&
					typeof filterText !== 'string' &&
					IS_NOT_NULL_OR_UNDEFINED(filterText)
				) {
					return true;
				}
			} else if (this.filterFieldsInputTypeMap?.get(filterField) === INPUT_TYPES.VOLUME) {
				if (operator === OPERATORS.BETWEEN && filterText && startValue && endValue) {
					return true;
				}
				if (operator !== OPERATORS.BETWEEN && filterText && startValue) {
					return true;
				}
			}
		}

		return false;
	}

	hasAnySorting(): boolean {
		for (let i = 0; i < this.sortFormArray.length; i++) {
			if (
				this.sortFormArray.at(i).get('sortField').value.length > 0 &&
				this.sortFormArray.at(i).get('direction').value.length > 0
			) {
				return true;
			}
		}

		return false;
	}

	addFilter() {
		this.formArray.push(
			this.formBuilder.group({
				filterField: new FormControl(''),
				filterText: '',
				filterCondition: new FormControl(this.AND),
				startValue: '',
				endValue: '',
				operator: '',
				isNotDeletable: false,
			})
		);

		this.addFilterOptions();
	}

	removeFilter(filterIndex: number) {
		this.formArray.removeAt(filterIndex);
		this.removeFilterOptions(filterIndex);
	}

	addSort() {
		this.sortFormArray.push(
			this.formBuilder.group({
				sortField: new FormControl(''),
				direction: this.defaultDirection,
			})
		);

		this.addSortOptions();
	}

	removeSort(index: number) {
		this.sortFormArray.removeAt(index);
		this.removeSortOptions(index);
	}

	clearFilters() {
		for (let i = this.formArray.length - 1; i >= 0; i--) {
			const filter = this.formArray.at(i).value;
			if (!filter.isNotDeletable) {
				this.formArray.removeAt(i);
				this.removeFilterOptions(i);
			}
		}
		if (!this.formArray.value.length) {
			this.addFilter();
			this.filterState = '';
		}

		if (!this.isStaticData) {
			this.emitClearFilter.emit(this.activeLink);
		}
		this.paginator.pageIndex = 0;
		this.dataSource.data = this.data;
		this.triggers.toArray()[0].togglePopover();
	}

	clearSort() {
		while (this.sortFormArray.length !== 0) {
			this.sortFormArray.removeAt(0);
			this.removeSortOptions(0);
		}
		this.addSort();
		this.sortState = '';

		if (!this.isStaticData) {
			this.emitClearSort.emit(this.activeLink);
		}
		// Re-apply filters if any exist, otherwise reset to original data
		if (this.isStaticData && this.hasAnyFilter()) {
			this.reapplyFilters();
		} else {
			this.dataSource.data = this.data;
		}
		this.triggers.toArray()[1].togglePopover();
	}

	private reapplyFilters() {
		this.applyFiltersToDataSource();
	}

	filterData() {
		if (this.isStaticData) {
			this.loadingData = true;
			this.applyFiltersToDataSource();
			this.loadingData = false;
		} else {
			this.emitFilter.emit(this.formArray.value);
		}
		this.paginator.pageIndex = 0;
		this.filterState = this.getFilterState();
		this.triggers.toArray()[0].togglePopover();
	}

	private applyFiltersToDataSource() {
		this.dataSource.data = this.data;

		const hasOrCondition = this.formArray.controls.some(
			(control) => control.get('filterCondition')?.value === this.OR
		);

		if (!hasOrCondition) {
			for (let i = 0; i < this.formArray.length; i++) {
				let filterField = this.formArray.at(i).get('filterField').value;
				let filterTextRaw = this.formArray.at(i).get('filterText').value;
				let filterText = this.normalizeToString(filterTextRaw);
				if (filterField.length > 0 && filterText.length > 0) {
					this.dataSource.data = this.dataSource.data.filter((element) => {
						let fieldValue = element[this.fieldMap[filterField]];
						let fieldValueStr = this.normalizeToString(fieldValue);
						return fieldValueStr.indexOf(filterText) > -1;
					});
				}
			}
		} else {
			this.dataSource.data = this.dataSource.data.filter((element) => {
				let hasValidFilter = false;
				for (let i = 0; i < this.formArray.length; i++) {
					let filterField = this.formArray.at(i).get('filterField').value;
					let filterTextRaw = this.formArray.at(i).get('filterText').value;
					let filterText = this.normalizeToString(filterTextRaw);
					if (filterField.length > 0 && filterText.length > 0) {
						hasValidFilter = true;
						let fieldValue = element[this.fieldMap[filterField]];
						let fieldValueStr = this.normalizeToString(fieldValue);
						if (fieldValueStr.indexOf(filterText) > -1) return true;
					}
				}
				return !hasValidFilter;
			});
		}
	}

	private normalizeToString(value: unknown): string {
		if (value == null) {
			return '';
		}
		if (typeof value === 'string') {
			return value.trim().toLowerCase();
		}
		if (typeof value === 'object') {
			const obj = value as Record<string, unknown>;
			const displayValue = obj['name'] ?? obj['label'] ?? obj['displayName'];
			if (displayValue != null) {
				return String(displayValue).trim().toLowerCase();
			}
			return String(value).trim().toLowerCase();
		}
		return String(value).trim().toLowerCase();
	}

	sortData() {
		if (this.isStaticData) {
			this.loadingData = true;
			for (let i = this.sortFormArray.length - 1; i >= 0; i--) {
				let sortField = this.sortFormArray.at(i).get('sortField').value;
				let sortDirection = this.sortFormArray.at(i).get('direction').value;
				this.dataSource.data = this.dataSource.data.sort((a, b) => {
					return this.compare(
						a[this.fieldMap[sortField]].trim().toLowerCase(),
						b[this.fieldMap[sortField]].trim().toLowerCase(),
						sortDirection === this.defaultDirection
					);
				});
			}
			this.loadingData = false;
		} else {
			this.emitSort.emit(this.sortFormArray.value);
		}
		this.paginator.pageIndex = 0;
		this.sortState = this.getSortState();
		this.triggers.toArray()[1].togglePopover();
	}

	compare(a: number | string, b: number | string, isAsc: boolean) {
		return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
	}

	getFilterState(): string {
		let count = 0;
		let field = '';
		if (!this.formArray?.length) {
			this.filterState = '';
			return field;
		}
		for (let i = 0; i < this.formArray.length; i++) {
			let filterField = this.formArray.at(i).get('filterField').value,
				filterText = '',
				operator = this.formArray.at(i).get('operator')?.value,
				startValue = this.formArray.at(i).get('startValue')?.value,
				endValue = this.formArray.at(i).get('endValue')?.value,
				inputType = this.filterFieldsInputTypeMap?.get(filterField),
				isInputValid = false,
				fieldValue = this.formArray.at(i).get('filterText')?.value;

			if (filterField === PROJECT_FIELDS.CUSTOMER) {
				filterText = this.formArray.at(i).get('filterText')?.value?.name?.trim();
			} else if (filterField === PROJECT_FIELDS.ASSIGNED_REPS) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			} else if (inputType === 'boolean') {
				filterText = this.formArray.at(i).get('filterText').value ? 'true' : 'false';
				isInputValid = true;
			} else if (typeof this.formArray.at(i).get('filterText')?.value === 'string') {
				filterText = this.formArray.at(i).get('filterText').value.trim();
			} else if (filterField === PROJECT_FIELDS.STAGE) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			} else if (filterField === PROJECT_FIELDS.TYPE) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			} else if (filterField === LOADS_AUTOMATION_FIELDS.JOB_STATUS) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			} else if (filterField === LOADS_AUTOMATION_FIELDS.USER) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			} else if (filterField === PROJECT_FIELDS.CARRIER) {
				const carrier = this.formArray.at(i).get('filterText')?.value;
				filterText = GET_CARRIER_BUSINESS_NAME(carrier?.businessName, carrier?.doingBusinessAs);
			} else if (filterField === LOAD_FIELDS.COMPANY) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			}

			// check if valid input number
			if (inputType === INPUT_TYPES.NUMBER) {
				filterText = this.formArray.at(i).get('filterText')?.value;
				if (operator === OPERATORS.BETWEEN && startValue && endValue) {
					isInputValid = true;
				}
				if (operator !== OPERATORS.BETWEEN && filterText) {
					isInputValid = true;
				}
			} else if (inputType === INPUT_TYPES.ALLOCATED_CARRIERS) {
				filterText = this.formArray.at(i).get('filterText')?.value;
				if (operator === OPERATORS.BETWEEN && startValue && endValue) {
					isInputValid = true;
				}
				if (operator !== OPERATORS.BETWEEN && filterText) {
					isInputValid = true;
				}
			} else if (inputType === INPUT_TYPES.STRING && filterText?.length && operator) {
				isInputValid = true;
			} else if (inputType === INPUT_TYPES.DATE) {
				isInputValid = true;
			} else if (inputType === INPUT_TYPES.VOLUME) {
				filterText = this.formArray.at(i).get('filterText')?.value;
				if (operator === OPERATORS.BETWEEN && filterText && startValue && endValue) {
					isInputValid = true;
				}
				if (operator !== OPERATORS.BETWEEN && filterText && startValue) {
					isInputValid = true;
				}
			} else if (
				(inputType === INPUT_TYPES.LOCATION ||
					inputType === INPUT_TYPES.CUSTOMER ||
					inputType == INPUT_TYPES.CARRIER ||
					inputType == INPUT_TYPES.CARRIER_ACCOUNTS ||
					inputType == INPUT_TYPES.USER_STATUS ||
					inputType == INPUT_TYPES.USER_ROLE) &&
				Object.keys(fieldValue || {}).length > 0
			) {
				isInputValid = true;
			} else if (inputType === INPUT_TYPES.TRAILER_TYPE) {
				isInputValid = true;
			} else if (inputType === INPUT_TYPES.STATE) {
				isInputValid = true;
			} else if (filterField === PROJECT_FIELDS.ARCHIVED) {
				filterText = this.formArray.at(i).get('filterText').value;
				isInputValid = true;
			}

			if (filterField?.length > 0 && isInputValid) {
				count++;
				if (field === '') {
					field = filterField;
				}
			}
		}
		return '(' + (count > 1 ? count.toString() : field) + ')';
	}

	getSortState(): string {
		let count = 0;
		let field = '';
		if (!this.sortFormArray?.length) {
			this.sortState = '';
			return field;
		}
		for (let i = 0; i < this.sortFormArray.length; i++) {
			let sortField = this.sortFormArray.at(i).get('sortField').value;
			let sortDirection = this.sortFormArray.at(i).get('direction').value;
			if (sortField.length > 0 && sortDirection.length > 0) {
				count++;
				if (field === '') {
					field = sortField;
				}
			}
		}
		return '(' + (count > 1 ? count.toString() : field) + ')';
	}

	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource.data.length;
		return numSelected === numRows;
	}

	masterToggle() {
		if (this.isAllSelected()) {
			this.isActionbuttonDisabled = true;
			this.selection.clear();
			this.notifySelectAll();
			return;
		}
		this.isActionbuttonDisabled = false;

		if (this.rowCheckboxIsDisabled) {
			let selectableRows = [];
			this.dataSource.data.forEach((row) => {
				if (!this.rowCheckboxIsDisabled(row)) {
					selectableRows.push(row);
				}
			});

			if (selectableRows.length === this.selection.selected.length) {
				this.selection.clear();
			} else {
				this.selection.select(...selectableRows);
			}
		} else {
			this.selection.select(...this.dataSource.data);
		}

		// The rowCheckboxIsDisabled branch above can end up clearing the selection, so
		// derive the button state from what is actually selected rather than assuming.
		this.isActionbuttonDisabled = this.selection.selected.length === 0;
		this.notifySelectAll();
	}

	/**
	 * CN-8950: tell the host component that the header select-all checkbox changed the
	 * selection. `selectRow` has always done this; `masterToggle` never did, so actions
	 * that are built from the current selection (Enable/Disable Workflow) were missing
	 * after select-all, or left stale from an earlier per-row selection.
	 *
	 * There is no single toggled row here, so the row argument is the last selected row
	 * (null when the toggle cleared the selection). Opt-in via `notifyRowOnSelectAll`.
	 */
	private notifySelectAll(): void {
		if (!this.notifyRowOnSelectAll || !this.rowOnSelect) {
			return;
		}

		const selected = this.selection.selected;
		this.rowOnSelect(selected[selected.length - 1] ?? null, selected.length > 0, selected);
	}

	selectRow(row: Object) {
		if (this.disableMultipleSelection) {
			this.selection.clear();
		}
		this.selection.toggle(row);
		if (this.rowOnSelect) {
			this.rowOnSelect(row, this.selection.isSelected(row), this.selection.selected);
		}
		if (this.selection.selected.length > 0) {
			this.isActionbuttonDisabled = false;
		} else {
			this.isActionbuttonDisabled = true;
		}
	}

	setLocationValue(value, form): void {
		form.get('filterText').setValue(value?.city);
	}

	setAssignedUsersValue(value, form): void {
		form.get('filterText').setValue(value);
	}

	setCustomer(value, form): void {
		form.get('filterText').setValue(value);
	}

	setCarrier(value, form): void {
		const carrier = {
			businessName: value?.businessName,
			doingBusinessAs: value?.doingBusinessAs,
			id: value?.id,
		};
		form.get('filterText').setValue(carrier);
	}

	setProject(value, form): void {
		form.get('filterText').setValue(value);
	}

	setAccount(value, form): void {
		form.get('filterText').setValue(value);
	}

	onFilterFieldSelection(filterIndex: number): void {
		const filterField = this.formArray.at(filterIndex).get('filterField').value,
			fieldOperatorInputType = this.filterFieldsInputTypeMap?.get(filterField);
		if (fieldOperatorInputType === INPUT_TYPES.DATE) {
			this.formArray.at(filterIndex).get('startValue')?.setValue(new Date());
			this.formArray.at(filterIndex).get('endValue')?.setValue(new Date());
			this.formArray.at(filterIndex).get('operator')?.setValue(OPERATORS.BETWEEN);
		}
		if (
			fieldOperatorInputType == INPUT_TYPES.NUMBER ||
			fieldOperatorInputType === INPUT_TYPES.STRING
		) {
			this.formArray.at(filterIndex).get('operator').setValue(OPERATORS.EQUAL);
			this.formArray.at(filterIndex).get('filterText').setValue(null);
		}
		if (this.filterFieldsInputTypeMap?.get(filterField) === INPUT_TYPES.TRAILER_TYPE) {
			this.formArray.at(filterIndex).get('operator').setValue('');
		}
		if (this.filterFieldsInputTypeMap?.get(filterField) === INPUT_TYPES.VOLUME) {
			this.formArray.at(filterIndex).get('operator').setValue(OPERATORS.EQUAL);
			this.formArray.at(filterIndex).get('filterText').setValue('');
			this.formArray.at(filterIndex).get('endValue').setValue(null);
			this.formArray.at(filterIndex).get('startValue').setValue(null);
		}
		if (fieldOperatorInputType === INPUT_TYPES.ALLOCATED_CARRIERS) {
			this.formArray.at(filterIndex).get('operator').setValue(OPERATORS.GREATER_THAN_EQUAL);
			this.formArray.at(filterIndex).get('filterText').setValue(null);
		}
		if (fieldOperatorInputType === INPUT_TYPES.BOOLEAN) {
			this.formArray.at(filterIndex).get('operator').setValue(null);
			this.formArray.at(filterIndex).get('filterText').setValue(null);
		}
		if (fieldOperatorInputType === INPUT_TYPES.LOCATION) {
			this.locationModel[filterField + filterIndex] = '';
		}
	}

	dateRangeUpdated(event: SelectedDateRange, form) {
		if (!event || !event.startDate || !event.endDate) {
			return;
		}
		// Always normalize to 'YYYY-MM-DD' — ignoring timezone completely
		const startYmd = TO_YMD(event.startDate);
		const endYmd = TO_YMD(event.endDate);

		if (!startYmd || !endYmd) {
			// Invalid input; don't crash
			return;
		}

		// Set values if controls exist
		const startCtrl = form?.get?.('startValue');
		const endCtrl = form?.get?.('endValue');

		if (startCtrl) startCtrl.setValue(startYmd);
		if (endCtrl) endCtrl.setValue(endYmd);
	}

	filterOptionsForFilterFields(value: string): string[] {
		const filterValue = value.toLowerCase();

		return this.filterFields.filter((option) => option.toLowerCase().includes(filterValue));
	}

	addFilterOptions() {
		this.filterOptions.push(
			this.formArray
				.at(this.formArray.length - 1)
				.get('filterField')
				.valueChanges.pipe(
					startWith(''),
					map((value) => this.filterOptionsForFilterFields(value || ''))
				)
		);
	}

	removeFilterOptions(index: number) {
		this.filterOptions.splice(index, 1);
	}

	filterOptionsForSortFields(value: string): string[] {
		const filterValue = value.toLowerCase();

		return this.sortFields.filter((option) => option.toLowerCase().includes(filterValue));
	}

	addSortOptions() {
		this.sortOptions.push(
			this.sortFormArray
				.at(this.sortFormArray.length - 1)
				.get('sortField')
				.valueChanges.pipe(
					startWith(''),
					map((value) => this.filterOptionsForSortFields(value || ''))
				)
		);
	}

	removeSortOptions(index: number) {
		this.sortOptions.splice(index, 1);
	}

	trailerTypeKeyPress(searchTerm: string, filterText, formArrayIndex: number) {
		if (!Array.isArray(filterText)) {
			filterText = [];
		}
		const result = this.utilService.detect(searchTerm, filterText);
		this.formArray.at(formArrayIndex).get('filterText').setValue(result);
	}

	importProjectsClicked() {
		this.importData.emit();
	}

	newProjectClicked() {
		this.createData.emit();
	}

	setFilterTextForInvalidFilterField(form, filterTrigger: MatAutocompleteTrigger): void {
		if (!this.filterFieldsInputTypeMap?.get(form.value?.filterField) || '') {
			filterTrigger?.closePanel();
			form.get('filterText').setValue('');
			setTimeout(() => {
				filterTrigger?.openPanel();
			}, 0);
		}
	}

	saveCustomFilter(tabName: string, isUpdate: boolean = false) {
		const url = this.urlService.saveUserSortsAndFilters();
		let filtersList = [].concat(this.formArray.value);
		if (this.userSortsAndFiltersType === USER_SORTS_AND_FILTERS_TYPE.LOADS) {
			let filter = {};
			if (this.isActiveTab) {
				filter['isActiveTab'] = this.isActiveTab;
			} else if (this.isOpenTab) {
				filter['isOpenTab'] = this.isOpenTab;
			} else if (this.isBookedTab) {
				filter['isBookedTab'] = this.isBookedTab;
			}
			filtersList = [filter].concat(filtersList);
		}

		let filterData = JSON.stringify(filtersList);
		let sortData = JSON.stringify(this.sortFormArray.value);

		if (tabName.length > 1) {
			if (
				this.sortFormArray.length === 1 &&
				this.sortFormArray.at(0).get('sortField').value.length < 1
			) {
				sortData = '[]';
			}

			const payload = {
				id: isUpdate ? this.customTabId : null,
				type: this.userSortsAndFiltersType,
				name: tabName,
				filter: filterData,
				sort: sortData,
			};
			this.restService.POST(url, payload).subscribe(
				(response: any) => {
					this.utilService.showSnackbar(
						'Custom tab saved successfully',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					this.getUserSortsAndFilters();
				},
				(error) => {
					console.error('Save custom tab error:', error);
					this.utilService.showSnackbar(
						'Failed to save custom tab',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
		} else {
			this.utilService.showSnackbar(
				'Please provide a name to your custom tab',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		}
	}

	firstAction() {
		if (this.actions.length > 0) {
			return this.actions[0];
		}
		return null;
	}

	setTabHeadersMaxWidth(): void {
		// Early return if using dropdown tabs or no tab header scroll element
		if (this.useDropdownTabs || !this.tabHeaderScroll) {
			return;
		}

		const tabHeadersList = document.getElementsByClassName('cc_tab_headers'),
			tabHeadersActions = document.getElementById('cc_tab_headers_actions'),
			matDataTableContainer = document.getElementById('mat_data_table');
		if (tabHeadersList?.length) {
			let maxWidth = 0;
			for (let tabIndex = 0; tabIndex < 3; tabIndex++) {
				if (tabHeadersList[tabIndex]?.clientWidth) {
					maxWidth += tabHeadersList[tabIndex].clientWidth;
				}
			}
			if (tabHeadersActions && matDataTableContainer) {
				const actionsWidth = tabHeadersActions.offsetWidth,
					tableWidth = matDataTableContainer.offsetWidth;
				if (
					tableWidth >
					actionsWidth +
						maxWidth +
						(this.tabScrollEnabled ? this.TAB_HEADER_NAVIGATION_BUTTONS_WIDTH : 0)
				) {
					this.tabHeadersMaxWidth =
						tableWidth - (actionsWidth + 8 + this.TAB_HEADER_NAVIGATION_BUTTONS_WIDTH);
				} else {
					this.tabHeadersMaxWidth = maxWidth ? maxWidth + 20 : null;
				}
			}
		}
		this.scrollPositionTimeoutId = setTimeout(() => {
			if (!this.useDropdownTabs && this.tabHeaderScroll?.nativeElement) {
				this.checkScrollPosition();
			}
		}, 300);
		setTimeout(() => {
			if (!this.useDropdownTabs && this.tabHeaderScroll?.nativeElement) {
				this.bringSelectedTabToFocus();
			}
		}, 400);
	}

	resetFilterAndSort() {
		while (this.formArray.length !== 0) {
			this.formArray.removeAt(0);
			this.removeFilterOptions(0);
		}
		this.addFilter();
		this.filterState = '';
		while (this.sortFormArray.length !== 0) {
			this.sortFormArray.removeAt(0);
			this.removeSortOptions(0);
		}
		this.addSort();
		this.sortState = '';
	}

	onProjectOperatorChange(event, form) {
		if (event?.value === OPERATORS.NOT_EXIST) {
			form.get('filterText').setValue([]);
		}
		if (!Array.isArray(form.get('filterText').value)) {
			form.get('filterText').setValue([]);
		}
	}

	scrollTabs(direction: 'left' | 'right') {
		// Guard against dropdown mode and missing elements
		if (this.useDropdownTabs || !this.tabHeaderScroll?.nativeElement) {
			return;
		}

		const scrollContainer = this.tabHeaderScroll.nativeElement;
		const amount = 150;
		scrollContainer.scrollBy({
			left: direction === 'left' ? -amount : amount,
			behavior: 'smooth',
		});

		this.scrollPositionTimeoutId = setTimeout(() => {
			if (!this.useDropdownTabs && this.tabHeaderScroll?.nativeElement) {
				this.checkScrollPosition();
			}
		}, 300);
	}

	checkScrollPosition() {
		const scrollContainer = this.tabHeaderScroll?.nativeElement;
		if (scrollContainer) {
			const isAtStart = scrollContainer.scrollLeft === 0;
			const isAtEnd =
				scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth;

			// Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
			this.scrollPositionTimeoutId = setTimeout(() => {
				this.isAtStart = isAtStart;
				this.isAtEnd = isAtEnd;
				this.changeDetectorRef.detectChanges();
			}, 0);
		}
	}

	bringSelectedTabToFocus() {
		// Guard against dropdown mode and missing elements
		if (this.useDropdownTabs || !this.tabHeaderScroll?.nativeElement) {
			return;
		}

		const scrollContainer = this.tabHeaderScroll.nativeElement;
		const activeTab = this.tabLinks.find((tab) =>
			tab.nativeElement.classList.contains('mat-tab-label-active')
		);
		if (!activeTab) return;

		const tabRect = activeTab.nativeElement.getBoundingClientRect();
		const containerRect = scrollContainer.getBoundingClientRect();

		if (tabRect.left < containerRect.left) {
			scrollContainer.scrollBy({
				left: tabRect.left - containerRect.left - 20,
				behavior: 'smooth',
			});
		} else if (tabRect.right > containerRect.right) {
			scrollContainer.scrollBy({
				left: tabRect.right - containerRect.right + 20,
				behavior: 'smooth',
			});
		}
	}

	// CN-8940: resolve the contact-control inputs for a row. Called from the cell
	// template, so the result is cached per row object — the mapper would otherwise
	// run on every change-detection pass for every visible row. The cache is keyed on
	// the row itself, so a reload (which produces new row objects) starts clean.
	private readonly contactActionsInputsCache = new WeakMap<object, ContactActionsInputs>();

	contactActionsFor(element: any): ContactActionsInputs {
		if (!this.showContactActions || !this.contactActionsInputs || !element) {
			return null;
		}

		let inputs = this.contactActionsInputsCache.get(element);

		if (!inputs) {
			inputs = this.contactActionsInputs(element);
			this.contactActionsInputsCache.set(element, inputs);
		}

		return inputs;
	}

	createActionsInjector(element: any): Injector {
		const inputs = this.customActionsInputs ? this.customActionsInputs(element) : {};
		const outputs = this.customActionsOutputs || {};

		return Injector.create({
			providers: [
				...Object.keys(inputs).map((key) => ({
					provide: key,
					useValue: inputs[key],
				})),
				...Object.keys(outputs).map((key) => ({
					provide: key,
					useValue: outputs[key],
				})),
			],
			parent: this.injector,
		});
	}

	onRowClick(event: any, row: any) {
		// Check if the click target is within the actions column
		const target = event.target as HTMLElement;
		const actionsCell = target.closest('td[mat-cell]');

		if (actionsCell) {
			// Check if this is the actions column by looking for the actions component
			// First try the configurable selector
			let actionsComponent = actionsCell.querySelector(this.actionsComponentSelector);

			// If not found, try the data attribute as fallback
			if (!actionsComponent) {
				actionsComponent = actionsCell.querySelector('[data-actions-component="true"]');
			}

			if (actionsComponent) {
				// Click is within actions column, don't trigger row click
				return;
			}
		}

		// Not in actions column, proceed with row click
		if (this.rowOnClickEnabled && this.rowOnClick) {
			this.rowOnClick(row);
		}
	}

	/**
	 * Determines if a tooltip should be shown for the given row
	 * @param row The row data
	 * @returns boolean indicating if tooltip should be displayed
	 */
	shouldShowTooltip(row: any): boolean {
		if (!this.enableRowTooltips || !row) return false;
		return row && row[this.tooltipDataFlag] === true;
	}

	/**
	 * Determines if a row should have lighter text styling
	 * @param row The row data
	 * @returns boolean indicating if lighter text should be applied
	 */
	shouldUseLighterText(row: any): boolean {
		if (!this.enableLighterText || !row) return false;
		return row && row[this.lighterTextDataFlag] === true;
	}

	/**
	 * Gets the CSS classes to apply to a row
	 * @param row The row data
	 * @returns string containing CSS classes
	 */
	getRowClasses(row: any): string {
		let classes = this.rowClasses || '';

		if (this.shouldUseLighterText(row)) {
			classes += ` ${this.lighterTextClass}`;
		}

		return classes.trim();
	}

	showCarrierModal(element: any, event: any): void {
		if (!this.enableCarrierModal || !this.carrierModalInputs) {
			return;
		}

		// Stop event propagation to prevent row click
		event.stopPropagation();

		const modalInputs = this.carrierModalInputs(element);
		const dialogRef = this.dialog.open(ContactDialogComponent, {
			width: '950px',
			data: {
				contactsCount: modalInputs.contactsCount,
				carrierId: modalInputs.carrierId,
				segmentEvent: modalInputs.segmentEvent,
				mc: modalInputs.mc,
				dot: modalInputs.dot,
				editPrimaryContact: modalInputs.editPrimaryContact,
				showCarrier: modalInputs.showCarrier,
				isFeedbackEnabled: modalInputs.isFeedbackEnabled,
				carrierBusinessName: modalInputs.carrierBusinessName,
				carrierHQ: modalInputs.carrierHQ,
				isLane: modalInputs.isLane,
				lanePath: modalInputs.lanePath,
				carrierLastFmcsaUpdate: modalInputs.carrierLastFmcsaUpdate,
				drivers: modalInputs.drivers,
				powerUnits: modalInputs.powerUnits,
				phone: modalInputs.phone,
				isCustomerCarrier: modalInputs.isCustomerCarrier,
				frequency: modalInputs.frequency,
				originDeadhead: modalInputs.originDeadhead,
				destinationDeadhead: modalInputs.destinationDeadhead,
				carrierBidContactId: modalInputs.carrierBidContactId,
			},
			disableClose: true,
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle modal close if needed
		});
	}

	loadTypeToggled(event: { label: string; value: string }) {
		this.selectedLoadType = event.value;
		this.paginator.pageIndex = 0;
		this.emitLoadTypeToggled.emit(event.value);
	}

	deleteCarrierLaneBrokerAccountMap(brokerAccountMap, carrierLane) {
		this.store.dispatch(
			CARRIER_LANES.DELETE_BROKER_ACCOUNT_MAP.DELETE({
				brokerAccountMap,
				carrierLaneIds: carrierLane.carrierLaneIds,
			})
		);
	}
}
